import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useChat } from '../context/ChatContext';
import { mediaUrl, uploadChatFiles, REACTION_EMOJIS } from '../api/chat';
import { listCustMessages, sendCustReply, custMute, custReact, custPinConversation } from '../api/customerChat';
import { fmtSize } from '../utils/chatFiles';
import { getSocket } from '../socket';
import { useToast } from './Toast';
import EmojiPicker from './EmojiPicker';

// ===== Chat KHÁCH HÀNG (tab "Khách hàng") =====
// Giao diện + thao tác Y HỆT chat nội bộ (dùng chung class CSS chat*), chỉ khác nguồn dữ liệu:
// KH nhắn từ web/app bidnhanh đổ vào qua webhook; NV trả lời sẽ đẩy ngược sang bidnhanh.
// Các helper nhỏ dưới đây LẶP LẠI từ ChatWidget.jsx (không import chéo để tránh vòng import).

const EMOJI_ONLY = /^(?:[\p{Extended_Pictographic}‍️⃣\u{1F3FB}-\u{1F3FF}]|\s)+$/u;
function isBigEmoji(t) {
  if (!t) return false;
  const s = t.trim();
  if (!s || !EMOJI_ONLY.test(s)) return false;
  return [...s].filter((c) => !/\s/.test(c)).length <= 6;
}
const initials = (name) => (name || '?').trim().split(/\s+/).slice(-1)[0]?.[0]?.toUpperCase() || '?';
const fmtTime = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (dt.toDateString() === new Date().toDateString()) return dt.toLocaleTimeString('vi', { hour: '2-digit', minute: '2-digit' });
  const p = (n) => String(n).padStart(2, '0');
  return `${p(dt.getDate())}/${p(dt.getMonth() + 1)}/${dt.getFullYear()}`;
};
const fmtMsgTime = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  const now = new Date();
  const hm = dt.toLocaleTimeString('vi', { hour: '2-digit', minute: '2-digit' });
  if (dt.toDateString() === now.toDateString()) return hm;
  const p = (n) => String(n).padStart(2, '0');
  const dm = `${p(dt.getDate())}/${p(dt.getMonth() + 1)}`;
  return dt.getFullYear() === now.getFullYear() ? `${hm} · ${dm}` : `${hm} · ${dm}/${dt.getFullYear()}`;
};
function AttachmentView({ a }) {
  const src = mediaUrl(a.url || a.data);
  if ((a.type || '').startsWith('image/')) {
    return <img className="chat-att-img" src={src} alt={a.name} title="Bấm để xem to" style={{ cursor: 'zoom-in' }} />;
  }
  return (
    <a className="chat-att-file" href={src} download={a.name} target="_blank" rel="noreferrer">
      📎 <span className="chat-att-name">{a.name}</span>
      <span className="chat-att-size">{fmtSize(a.size)}</span>
    </a>
  );
}

// Ai được thấy hội thoại KH "chưa gán" (đồng bộ backend accessScope): GĐ/CV + Phòng CSKH/DVKH.
export const canSeeAllKh = (me) =>
  me?.role === 'GD' || me?.role === 'CV' || (me?.departments || []).some((d) => ['Phòng CSKH', 'Phòng DVKH'].includes(d));

// Danh sách hội thoại KH — cùng kiểu dòng với danh sách nội bộ.
export function CustomerConversationList() {
  const { custConvs, openConv, windows, me, patchCustConv } = useChat();
  const admin = canSeeAllKh(me);
  // Ghim hội thoại KH lên đầu (riêng mình, tối đa 3) — giống danh sách nội bộ.
  const togglePinConv = async (e, c) => {
    e.stopPropagation();
    try {
      const r = await custPinConversation(c.id, !c.pinned);
      patchCustConv(c.id, { pinned: r.pinned });
    } catch (err) { alert(err?.response?.data?.message || 'Ghim thất bại'); }
  };
  if (!custConvs.length)
    return (
      <div className="chatempty">
        Chưa có khách hàng nào nhắn tin.
        <br />
        <span className="small muted">Tin nhắn từ web/app bidnhanh sẽ tự hiện ở đây.</span>
      </div>
    );
  return (
    <div className="chatlist">
      {[...custConvs].sort((a, b) => (a.pinned === b.pinned ? 0 : a.pinned ? -1 : 1)).map((c) => {
        const opened = windows.some((w) => String(w.id) === String(c.id));
        return (
          <div
            key={c.id}
            className={'chatlist-row' + (c.unread ? ' unread' : '') + (opened ? ' active' : '')}
            onClick={() => openConv(c)}
          >
            <div className="chatlist-ava">
              <div className="chat-avatar cust">{initials(c.name)}</div>
              {c.unread > 0 && <span className="chatlist-dot" />}
            </div>
            <div className="chatlist-main">
              <div className="chatlist-top">
                <span className="chatlist-name">
                  {c.pinned ? '📌 ' : ''}
                  {c.muted ? '🔕 ' : ''}
                  {c.name}
                  {admin && !c.assignedTo && <span className="chattag-warn">chưa gán</span>}
                </span>
                <span className="chatlist-time">{fmtTime(c.lastMessage?.at)}</span>
              </div>
              <div className="chatlist-preview">
                {c.lastMessage?.text || 'Bắt đầu trò chuyện…'}
                {admin && c.assignedName ? <span className="small muted"> · {c.assignedName}</span> : null}
              </div>
            </div>
            {c.unread > 0 && <span className="chatlist-badge">{c.unread}</span>}
            <button type="button" className={'chatlist-pinbtn' + (c.pinned ? ' on' : '')}
              title={c.pinned ? 'Bỏ ghim hội thoại' : 'Ghim hội thoại lên đầu (tối đa 3)'}
              onClick={(e) => togglePinConv(e, c)}>📌</button>
          </div>
        );
      })}
    </div>
  );
}

// Trạng thái ĐẨY SANG BIDNHANH của tin NV gửi (hiện cạnh giờ). 'not-configured' = chưa nối API.
function deliveryLabel(m) {
  if (m.direction !== 'out') return '';
  if (m.status === 'sent') return ' · ✓ đã tới bidnhanh';
  if (m.status === 'failed') return m.error === 'not-configured' ? ' · đã lưu, chờ nối bidnhanh' : ' · ⚠ lỗi đẩy sang bidnhanh';
  return '';
}

// Cửa sổ chat với 1 KH — cùng kiểu cửa sổ chat nội bộ.
// onTitleClick (chỉ ChatPage truyền): bấm TÊN KH trên đầu khung -> mở/đóng bảng thông tin bên phải.
export function CustomerChatWindow({ conv, front, onTitleClick }) {
  const { me, subscribeMessages, setConvMinimized, patchCustConv, bumpCustConv, closeWindow, setOpen } = useChat();
  const isMobile = () => typeof window !== 'undefined' && window.innerWidth <= 980;
  const toast = useToast();
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(true);
  const [text, setText] = useState('');
  const [atts, setAtts] = useState([]);
  const [replyTo, setReplyTo] = useState(null); // { id, senderName, text }
  const [imgView, setImgView] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [sending, setSending] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [minUnread, setMinUnread] = useState(0);
  const [reactPickerFor, setReactPickerFor] = useState(null);
  const [peek, setPeek] = useState(null);
  const [newBelow, setNewBelow] = useState(0);
  const [size, setSize] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem('ft_chat_size') || 'null');
      if (s && s.w && s.h) return { w: s.w, h: s.h };
    } catch {}
    return null;
  });
  const emojiBtnRef = useRef(null);
  const minimizedRef = useRef(false);
  const endRef = useRef(null);
  const msgsRef = useRef(null);
  const lastMsgIdRef = useRef(null);
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const fileRef = useRef(null);
  const unreadRef = useRef(null);
  const initRef = useRef(false);
  const unreadSnapRef = useRef(conv.unread || 0);

  const myId = String(me.id || me._id);

  const toggleMinimize = useCallback((next) => {
    const on = typeof next === 'boolean' ? next : !minimizedRef.current;
    minimizedRef.current = on;
    setMinimized(on);
    setConvMinimized(conv.id, on);
    if (!on) { setMinUnread(0); import('../api/customerChat').then(({ custMarkRead }) => custMarkRead(conv.id).catch(() => {})); }
  }, [conv.id, setConvMinimized]);
  useEffect(() => () => setConvMinimized(conv.id, false), [conv.id, setConvMinimized]);

  // Tải tin khi mở cửa sổ.
  useEffect(() => {
    let alive = true;
    setLoadingMsgs(true);
    initRef.current = false;
    unreadSnapRef.current = conv.unread || 0;
    listCustMessages(conv.id)
      .then((msgs) => alive && setMessages(msgs))
      .finally(() => alive && setLoadingMsgs(false));
    return () => { alive = false; };
  }, [conv.id]);

  // Tin mới realtime (qua bus của ChatContext — sự kiện custchat:message).
  useEffect(() => {
    const unsub = subscribeMessages(conv.id, (payload) => {
      if (minimizedRef.current && payload.direction === 'in' && !payload.clientId) setMinUnread((n) => n + 1);
      setMessages((prev) => {
        if (payload.clientId && prev.some((m) => m.clientId && m.clientId === payload.clientId)) {
          return prev.map((m) => (m.clientId === payload.clientId ? { ...payload } : m));
        }
        return prev.some((m) => String(m._id) === String(payload._id)) ? prev : [...prev, payload];
      });
    });
    return unsub;
  }, [conv.id, subscribeMessages]);

  // DỰ PHÒNG socket chập chờn: ~5s tải lại + gộp (giữ tin đang gửi/lỗi) — giống chat nội bộ.
  useEffect(() => {
    const tick = () => {
      if (document.hidden) return;
      listCustMessages(conv.id)
        .then((msgs) => setMessages((prev) => {
          const serverIds = new Set(msgs.map((m) => String(m._id)));
          const pending = prev.filter((m) => m.clientId && (m.pending || m.failed) && !serverIds.has(String(m._id)));
          const next = [...msgs, ...pending];
          if (next.length === prev.length && next.every((m, i) => String(m._id) === String(prev[i]?._id))) return prev;
          return next;
        }))
        .catch(() => {});
    };
    const t = setInterval(tick, 5000);
    return () => clearInterval(t);
  }, [conv.id]);

  // Cảm xúc realtime.
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;
    const onReaction = (p) => {
      if (String(p.conversation) !== String(conv.id)) return;
      setMessages((prev) => prev.map((m) => (String(m._id) === String(p.messageId) ? { ...m, reactions: p.reactions } : m)));
    };
    socket.on('custchat:reaction', onReaction);
    return () => socket.off('custchat:reaction', onReaction);
  }, [conv.id]);

  const isNearBottom = () => {
    const el = msgsRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  };
  const jumpToLatest = () => {
    const el = msgsRef.current;
    if (el) el.scrollTop = el.scrollHeight;
    setNewBelow(0);
  };

  // Cuộn: lần đầu tới tin chưa đọc; tin mới chỉ tự cuộn khi của mình hoặc đang ở gần đáy.
  useEffect(() => {
    if (!messages.length) return;
    const last = messages[messages.length - 1];
    const lastId = String(last.clientId || last._id);
    if (!initRef.current) {
      initRef.current = true;
      lastMsgIdRef.current = lastId;
      if (unreadRef.current) unreadRef.current.scrollIntoView({ block: 'start' });
      else endRef.current?.scrollIntoView();
      return;
    }
    if (lastMsgIdRef.current === lastId) return;
    lastMsgIdRef.current = lastId;
    if (String(last.sender?._id || '') === myId || isNearBottom()) {
      endRef.current?.scrollIntoView({ behavior: 'smooth' });
    } else {
      setNewBelow((n) => n + 1);
    }
  }, [messages]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (atts.length) endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [atts]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ô nhập tự cao theo nội dung (tối đa ~5 dòng) — giống chat nội bộ.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 112) + 'px';
  }, [text]); // eslint-disable-line react-hooks/exhaustive-deps

  function addFiles(fileList) {
    const items = [...fileList].map((f) => ({
      file: f, name: f.name, type: f.type, size: f.size,
      preview: f.type.startsWith('image/') ? URL.createObjectURL(f) : null,
    }));
    setAtts((prev) => [...prev, ...items]);
  }
  function onPaste(e) {
    const imgs = [...(e.clipboardData?.items || [])].filter((it) => it.kind === 'file').map((it) => it.getAsFile()).filter(Boolean);
    if (imgs.length) { e.preventDefault(); addFiles(imgs); }
  }
  function onDrop(e) {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
  }

  // Đóng bảng emoji khi click ra ngoài.
  useEffect(() => {
    if (!showEmoji) return;
    const onDown = (e) => {
      if (e.target.closest?.('.emoji-pop')) return;
      if (emojiBtnRef.current?.contains(e.target)) return;
      setShowEmoji(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [showEmoji]);

  const insertEmoji = useCallback((emo) => {
    const el = inputRef.current;
    const start = el ? el.selectionStart : null;
    const end = el ? el.selectionEnd : null;
    setText((prev) => {
      const a = start ?? prev.length;
      const b = end ?? prev.length;
      return prev.slice(0, a) + emo + prev.slice(b);
    });
    setTimeout(() => {
      el?.focus();
      const p = (start ?? 0) + emo.length;
      el?.setSelectionRange(p, p);
    }, 0);
  }, []);

  const sendSticker = useCallback(async (emo) => {
    setShowEmoji(false);
    try {
      const msg = await sendCustReply(conv.id, emo, {});
      setMessages((prev) => (prev.some((m) => String(m._id) === String(msg._id)) ? prev : [...prev, msg]));
      bumpCustConv(conv.id, msg);
    } catch (err) {
      alert(err?.response?.data?.message || err?.message || 'Gửi sticker thất bại');
    }
  }, [conv.id, bumpCustConv]);

  async function submit(e) {
    e?.preventDefault();
    const t = text.trim();
    if ((!t && !atts.length) || sending) return;
    const reply = replyTo;
    const replySnap = reply ? { message: reply.id, senderName: reply.senderName, text: reply.text } : null;
    const sentAtts = atts;
    setText('');
    setAtts([]);
    setReplyTo(null);

    // Đường nhanh: chỉ chữ -> hiện ngay (optimistic), thay bằng bản thật khi server trả về.
    if (!sentAtts.length) {
      const clientId = 'tmp_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
      const temp = { _id: clientId, clientId, direction: 'out', sender: { _id: myId, name: me.name }, senderName: me.name, text: t, attachments: [], replyTo: replySnap, createdAt: new Date().toISOString(), pending: true };
      setMessages((prev) => [...prev, temp]);
      try {
        const msg = await sendCustReply(conv.id, t, { clientId, replyTo: reply?.id });
        setMessages((prev) => {
          if (prev.some((m) => String(m._id) === String(msg._id) && m.clientId !== clientId)) {
            return prev.filter((m) => m.clientId !== clientId);
          }
          return prev.map((m) => (m.clientId === clientId ? { ...msg } : m));
        });
        bumpCustConv(conv.id, msg);
      } catch {
        setMessages((prev) => prev.map((m) => (m.clientId === clientId ? { ...m, pending: false, failed: true } : m)));
      }
      return;
    }

    // Có đính kèm: upload trước (dùng chung kho tệp chat nội bộ).
    setSending(true);
    try {
      const metas = await uploadChatFiles(sentAtts.map((a) => a.file));
      const msg = await sendCustReply(conv.id, t, { attachments: metas, replyTo: reply?.id });
      setMessages((prev) => (prev.some((m) => String(m._id) === String(msg._id)) ? prev : [...prev, msg]));
      bumpCustConv(conv.id, msg);
      sentAtts.forEach((a) => a.preview && URL.revokeObjectURL(a.preview));
    } catch (err) {
      setText(t);
      setAtts(sentAtts);
      alert(err?.response?.data?.message || err?.message || 'Gửi tệp thất bại');
    } finally {
      setSending(false);
    }
  }

  const resend = useCallback(async (failed) => {
    setMessages((prev) => prev.map((m) => (m.clientId === failed.clientId ? { ...m, pending: true, failed: false } : m)));
    try {
      const msg = await sendCustReply(conv.id, failed.text, { clientId: failed.clientId });
      setMessages((prev) => prev.map((m) => (m.clientId === failed.clientId ? { ...msg } : m)));
      bumpCustConv(conv.id, msg);
    } catch {
      setMessages((prev) => prev.map((m) => (m.clientId === failed.clientId ? { ...m, pending: false, failed: true } : m)));
    }
  }, [conv.id, bumpCustConv]);

  // Tắt/bật âm hội thoại KH (riêng mình).
  const muted = !!conv.muted;
  const doMute = useCallback(async () => {
    try {
      const r = await custMute(conv.id, !muted);
      patchCustConv(conv.id, { muted: r.muted });
      toast(r.muted ? '🔕 Đã tắt âm hội thoại này' : '🔔 Đã bật lại thông báo');
    } catch (e) { toast(e?.response?.data?.message || 'Không đổi được', 'danger'); }
  }, [conv.id, muted, patchCustConv, toast]);

  const startReply = useCallback((m) => {
    const preview = m.text || ((m.attachments || []).length ? 'đã gửi tệp đính kèm' : '');
    setReplyTo({ id: m._id, senderName: m.direction === 'in' ? (m.senderName || conv.name) : (m.sender?.name || m.senderName || ''), text: String(preview).slice(0, 200) });
    setTimeout(() => {
      const el = inputRef.current;
      if (el) { el.focus(); const n = el.value.length; el.setSelectionRange(n, n); }
    }, 0);
  }, [conv.name]);

  // Thả/đổi/bỏ cảm xúc (chỉ tin không phải của mình) — cập nhật lạc quan.
  const doReact = useCallback((m, emoji) => {
    setReactPickerFor(null);
    setMessages((prev) => prev.map((mm) => {
      if (String(mm._id) !== String(m._id)) return mm;
      const others = (mm.reactions || []).filter((r) => String(r.user) !== myId);
      const cur = (mm.reactions || []).find((r) => String(r.user) === myId);
      const next = !emoji || (cur && cur.emoji === emoji) ? others : [...others, { user: myId, name: me.name, emoji }];
      return { ...mm, reactions: next };
    }));
    custReact(m._id, emoji).catch(() => listCustMessages(conv.id).then(setMessages).catch(() => {}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myId, me.name, conv.id]);

  const scrollToMsg = useCallback((id) => {
    const el = id && document.getElementById('kmsg-' + id);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('chatmsg-flash');
    setTimeout(() => el.classList.remove('chatmsg-flash'), 1400);
  }, []);

  // Tin KH chưa đọc đầu tiên (để kẻ vạch "Tin nhắn mới").
  const firstUnreadId = (() => {
    const n = unreadSnapRef.current;
    if (!n) return null;
    const incoming = messages.filter((m) => m.direction === 'in');
    if (!incoming.length) return null;
    return incoming[Math.max(0, incoming.length - n)]?._id || null;
  })();

  const messageEls = useMemo(
    () =>
      messages.map((m) => {
        const staffSide = m.direction === 'out'; // NV/GĐ trả lời -> bên phải (như tin của mình)
        const mine = staffSide && String(m.sender?._id || '') === myId;
        const isFirstUnread = String(m._id) === String(firstUnreadId);
        const who = staffSide ? (m.sender?.name || m.senderName) : (m.senderName || conv.name);
        return (
          <div className="chatmsg-row" key={m._id} id={'kmsg-' + m._id}>
            {isFirstUnread && (
              <div className="chat-newdivider" ref={unreadRef}>
                <span>Tin nhắn mới</span>
              </div>
            )}
            <div className={'chatmsg' + (staffSide ? ' mine' : '') + (m.pending ? ' pending' : '') + (m.failed ? ' failed' : '')}>
              {/* Tên người viết: tin KH luôn hiện tên KH; tin NV khác (GĐ xem) hiện tên NV */}
              {!mine && (
                <div className="chatmsg-who">{who}</div>
              )}
              <div className="chatmsg-line">
                <div className="chatmsg-content">
                  {m.replyTo && (
                    <div className="chatmsg-quote" onClick={() => scrollToMsg(m.replyTo.message)} title="Xem tin gốc">
                      <span className="chatmsg-quote-name">{m.replyTo.senderName || 'Tin nhắn'}</span>
                      <span className="chatmsg-quote-text">{m.replyTo.text || 'đã gửi tệp đính kèm'}</span>
                    </div>
                  )}
                  <div
                    className={'chatmsg-body' + (staffSide ? ' mine' : '')}
                    onClick={!mine ? () => setReactPickerFor((v) => (v === m._id ? null : m._id)) : undefined}
                    title={!mine ? 'Bấm để thả cảm xúc' : undefined}
                  >
                    {m.text && (
                      <div className={'chatmsg-bubble' + (isBigEmoji(m.text) ? ' big-emoji' : '')}>{m.text}</div>
                    )}
                    {(m.attachments || []).map((a, i) => (
                      <div className="chatmsg-att" key={i}>
                        <AttachmentView a={a} />
                      </div>
                    ))}
                    <div className="chatmsg-corner">
                      <button className="chatmsg-cbtn" title="Trả lời tin này" onClick={(e) => { e.stopPropagation(); startReply(m); }}>↩</button>
                    </div>
                    {!mine && reactPickerFor === m._id && (
                      <div className="chatmsg-react-picker" onClick={(e) => e.stopPropagation()} onMouseLeave={() => setReactPickerFor(null)}>
                        {REACTION_EMOJIS.map((e) => (
                          <button key={e} type="button" className="chatmsg-react-opt" onClick={() => doReact(m, e)}>{e}</button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {(m.reactions || []).length > 0 && (
                <div className="chatmsg-reacts">
                  {Object.entries(
                    (m.reactions || []).reduce((acc, r) => {
                      (acc[r.emoji] = acc[r.emoji] || []).push(r.name || 'Ai đó');
                      return acc;
                    }, {})
                  ).map(([emoji, names]) => {
                    const meReacted = (m.reactions || []).some((r) => String(r.user) === myId && r.emoji === emoji);
                    return (
                      <button
                        key={emoji}
                        type="button"
                        className={'chatmsg-react-badge' + (meReacted ? ' me' : '')}
                        title="Xem ai đã thả cảm xúc"
                        onClick={() => setPeek({ title: 'Cảm xúc', lines: (m.reactions || []).map((r) => ({ icon: r.emoji, name: r.name || 'Ai đó' })) })}
                      >
                        <span>{emoji}</span> {names.length}
                      </button>
                    );
                  })}
                </div>
              )}
              {mine && m.failed ? (
                <div className="chatmsg-time" style={{ color: 'var(--danger)' }}>
                  Gửi lỗi · <button type="button" className="chatmsg-resend" onClick={() => resend(m)}>Gửi lại</button>
                </div>
              ) : (
                <div className="chatmsg-time">{m.pending ? 'Đang gửi…' : fmtMsgTime(m.createdAt) + deliveryLabel(m)}</div>
              )}
            </div>
          </div>
        );
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [messages, myId, firstUnreadId, reactPickerFor, doReact, resend, startReply, scrollToMsg]
  );

  // Kéo giãn cửa sổ (chia sẻ kích thước với chat nội bộ qua ft_chat_size).
  const startResize = useCallback((e) => {
    if (window.innerWidth <= 980) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = panelRef.current?.getBoundingClientRect();
    const startW = rect?.width || 372;
    const startH = rect?.height || 520;
    const startX = e.clientX;
    const startY = e.clientY;
    const onMove = (ev) => {
      const w = Math.min(720, Math.max(300, startW + (startX - ev.clientX)));
      const h = Math.min(Math.round(window.innerHeight * 0.92), Math.max(360, startH + (startY - ev.clientY)));
      setSize({ w, h });
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = '';
      setSize((s) => { if (s) { try { localStorage.setItem('ft_chat_size', JSON.stringify(s)); } catch {} } return s; });
    };
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, []);

  const panelStyle = size && !minimized ? { width: size.w, height: size.h } : undefined;

  return (
    <div ref={panelRef} style={panelStyle} className={'chatpanel chatpanel-conv' + (front ? ' front' : '') + (minimized ? ' min' : '')}>
      {!minimized && <div className="chatpanel-resize" onMouseDown={startResize} title="Kéo để thay đổi kích thước" />}
      {peek && (
        <div className="chat-peek-overlay" onClick={() => setPeek(null)}>
          <div className="chat-peek" onClick={(e) => e.stopPropagation()}>
            <div className="chat-peek-head">
              <b>{peek.title} · {peek.lines.length}</b>
              <button type="button" className="chat-members-x" onClick={() => setPeek(null)} title="Đóng">×</button>
            </div>
            <div className="chat-peek-list">
              {peek.lines.map((l, i) => (
                <div key={i} className="chat-peek-row">
                  <span className="chat-peek-icon">{l.icon}</span>
                  <span className="chat-peek-name">{l.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      <div className="chathead" onClick={() => minimized && toggleMinimize(false)}>
        <button className="chathead-btn chathead-back" onClick={(e) => { e.stopPropagation(); closeWindow(conv.id); }} title="Quay lại danh sách">‹</button>
        <span className="chat-avatar sm chathead-ava cust">{initials(conv.name)}</span>
        <div
          className="chathead-title"
          onClick={onTitleClick ? (e) => { e.stopPropagation(); onTitleClick(); } : undefined}
          style={onTitleClick ? { cursor: 'pointer' } : undefined}
          title={onTitleClick ? 'Bấm để mở/đóng bảng thông tin' : undefined}
        >
          {conv.name}
          {minimized && minUnread > 0 && <span className="chathead-unread" style={{ marginLeft: 8 }}>{minUnread > 99 ? '99+' : minUnread}</span>}
          {!minimized && (
            <span className="small" style={{ opacity: .85, fontWeight: 500 }}>
              {conv.custCode ? ` · ${conv.custCode}` : ''}{conv.assignedName ? ` · ${conv.assignedName}` : ' · chưa gán'}
            </span>
          )}
        </div>
        {!minimized && (
          <button className="chathead-btn" onClick={(e) => { e.stopPropagation(); doMute(); }}
            title={muted ? 'Đang tắt âm — bấm để bật lại thông báo' : 'Tắt âm thông báo hội thoại này'}>
            {muted ? '🔕' : '🔔'}
          </button>
        )}
        <button className="chathead-btn chathead-min" onClick={(e) => { e.stopPropagation(); toggleMinimize(); }} title={minimized ? 'Mở lại' : 'Thu nhỏ'}>
          {minimized ? '▢' : '—'}
        </button>
        <button className="chathead-btn" onClick={(e) => { e.stopPropagation(); if (isMobile()) setOpen(false); else closeWindow(conv.id); }} title="Đóng cửa sổ chat">
          ×
        </button>
      </div>
      <div className="chatbody">
        <div
          className={'chatthread' + (dragOver ? ' dragover' : '')}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={(e) => { if (e.currentTarget === e.target) setDragOver(false); }}
          onDrop={onDrop}
        >
          {dragOver && <div className="chat-droplay">Thả tệp để gửi</div>}
          <div
            className="chatmsgs"
            ref={msgsRef}
            onScroll={() => { if (newBelow && isNearBottom()) setNewBelow(0); }}
            onClick={(e) => { if (e.target.classList.contains('chat-att-img')) setImgView(e.target.src); }}
          >
            {loadingMsgs ? <div className="chatempty">Đang tải…</div> : messageEls}
            <div ref={endRef} />
          </div>

          {newBelow > 0 && (
            <button type="button" className="chat-jump-latest" onClick={jumpToLatest}>
              ↓ {newBelow} tin nhắn mới
            </button>
          )}

          {atts.length > 0 && (
            <div className="chat-att-strip">
              {atts.map((a, i) => (
                <div className="chat-att-chip" key={i}>
                  {a.preview ? <img src={a.preview} alt="" /> : <span className="chat-att-doc">📎 {a.name}</span>}
                  <button type="button" className="chat-att-x" onClick={() => setAtts((prev) => prev.filter((_, j) => j !== i))}>
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          {replyTo && (
            <div className="chat-reply-bar">
              <div className="chat-reply-info">
                <span className="chat-reply-label">↩ Trả lời {replyTo.senderName}</span>
                <span className="chat-reply-text">{replyTo.text || 'đã gửi tệp đính kèm'}</span>
              </div>
              <button type="button" className="chat-reply-x" onClick={() => setReplyTo(null)} title="Huỷ trả lời">×</button>
            </div>
          )}

          {imgView && (
            <div className="chat-lightbox" onClick={() => setImgView(null)}>
              <button type="button" className="chat-lightbox-x" onClick={() => setImgView(null)} title="Đóng ảnh">×</button>
              <img src={imgView} alt="" onClick={(e) => e.stopPropagation()} />
            </div>
          )}

          <form className="chatcompose" onSubmit={submit}>
            {showEmoji && <EmojiPicker onPick={insertEmoji} onSend={sendSticker} />}
            <button
              ref={emojiBtnRef}
              type="button"
              className={'chathead-btn chat-attbtn' + (showEmoji ? ' on' : '')}
              title="Sticker & emoji"
              onClick={() => setShowEmoji((v) => !v)}
            >
              😊
            </button>
            <button type="button" className="chathead-btn chat-attbtn" title="Đính kèm tệp" onClick={() => fileRef.current?.click()}>
              📎
            </button>
            <input ref={fileRef} type="file" multiple style={{ display: 'none' }} onChange={(e) => addFiles(e.target.files)} />
            <textarea
              ref={inputRef}
              rows={1}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onPaste={onPaste}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent?.isComposing) {
                  e.preventDefault();
                  e.currentTarget.form?.requestSubmit();
                }
              }}
              placeholder="Nhắn cho khách… (Enter gửi · Shift+Enter xuống dòng)"
              autoFocus={front}
            />
            <button type="submit" className="btn sm" disabled={sending || (!text.trim() && !atts.length)}>
              {sending ? '…' : 'Gửi'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
